import type { IProviderConfig, IUserInfo } from "@/account/account-models";
import { EUserType } from "@/account/account-models";
import { API_ROOT } from "@/globals";
import type { IDictionary } from "@/models";
import {
  addOnlineUser,
  addOnlineUsers,
  removeOnlineUser,
  setUnreadMessages,
  setUnreadMessagesFavorites,
} from "@/pubnub/pubnub-action-creators";
import type { PubnubActions } from "@/pubnub/pubnub-actions";
import { isOnline } from "@/utils/time";
import { defined } from "@/utils/variable-evaluation";
import deepEqual from "fast-deep-equal";
import type { IChatInfo } from "./chats-models";

export enum EAltChatType {
  Following = "affiliate",
}

export enum EChatFilterType {
  Search = "search",
  Category = "category",
}

export enum EChatFilterCategory {
  All = "all",
  Online = "online",
  Favorites = "favorites",
  PaidChat = "paid_chat",
  Fans = "fans",
  Followers = "followers",
  Following = "following",
  Subscribers = "subscribers",
  Subscribed = "subscribed",
  Unreplied = "unreplied",
  Unread = "unread",
  Vip = "vip",
  Sent = "sent",
  Recent = "recent",
  Hidden = "hidden",
  Creators = "creators",
}

export const DEFAULT_CHAT_FILTERS_STATE = {
  [EChatFilterType.Search]: {
    type: EChatFilterType.Search,
    value: null,
  },
  [EChatFilterType.Category]: {
    type: EChatFilterType.Category,
    value: EChatFilterCategory.All,
  },
};

export const TO_API_KEY: Record<EChatFilterType, string> = {
  [EChatFilterType.Search]: "search",
  [EChatFilterType.Category]: "type",
};

export const TO_API_VALUE: Partial<
  Record<EChatFilterType, Partial<Record<EChatFilterCategory, string>>>
> = {
  [EChatFilterType.Category]: {
    [EChatFilterCategory.Fans]: "fans2",
  },
};

export const FROM_API_VALUE = {
  [EChatFilterType.Category]: {
    fans2: EChatFilterCategory.Fans,
  },
};

export interface IChatFilter {
  type?: EChatFilterType;
  // TODO: shape depends on `type` (a string for Search, an
  // EChatFilterCategory for Category, etc.) - needs a discriminated union
  // keyed by `type` to type properly, not a quick fix.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
}

export function isPaidChat(
  chat: IChatInfo | null | undefined,
  isPaidChatV2Enabled: boolean
) {
  if (!chat || !isPaidChatV2Enabled) return false;
  if (defined(chat.is_paid_chat)) return !!chat.is_paid_chat;
  return chat.credit_rate > 0 && !chat.is_free_for_subscriber;
}

export function hasPaidChatV2State(chat?: IChatInfo | null) {
  return (
    !!chat &&
    (defined(chat.is_paid_chat) ||
      defined(chat.auto_unlock) ||
      defined(chat.paid_chat_request))
  );
}

export function isPaidChatCurrentlyOff(
  chat: IChatInfo | null | undefined,
  isPaidChatV2Enabled: boolean
) {
  return isPaidChat(chat, isPaidChatV2Enabled) && !chat?.auto_unlock;
}

export function isChatFreeTrial(chat: IChatInfo) {
  return !!chat.is_intro && !chat.intro_ended;
}

// Why the creator can't edit this chat's rate (null = can). The server refuses
// the same cases; force-free mirrors the global Chat Pricing, which is hidden
// for those creators.
export type TChatRateLock =
  "force_free" | "no_permission" | "subscriber" | "free_trial";

export function getChatRateLock(
  user: IUserInfo,
  chat: IChatInfo
): TChatRateLock | null {
  if (user.force_free_chats && chat.created_under_force_free_chats) {
    return "force_free";
  }
  if (!user.can_change_chat_rate) return "no_permission";
  // The subscription, not just the chat flag: a fan who turned the free chat
  // into a paid one clears the flag, but the creator stays locked out.
  if (
    chat.is_free_for_subscriber ||
    (user.subscription_free_chats && chat.other_user.is_subscriber)
  ) {
    return "subscriber";
  }
  if (isChatFreeTrial(chat)) return "free_trial";
  return null;
}

// Change Rates holds the chat rate and the call rate, so it is only worth
// opening when at least one of them can be edited.
export function canOpenChatRates(user: IUserInfo, chat?: IChatInfo | null) {
  return (
    !!chat &&
    user.type === EUserType.Featured &&
    chat.other_user.type === EUserType.User &&
    (!!chat.call || getChatRateLock(user, chat) === null)
  );
}

// A free trial chat in the chat list: highlighted for both sides (for the
// model to reply and turn the fan into a customer).
export function isFreeTrialChatInList(
  user: IUserInfo,
  chat: IChatInfo,
  isPaidChatV2Enabled: boolean
) {
  return (
    isPaidChatV2Enabled &&
    isEligiblePaidChatPair(user, chat.other_user) &&
    isChatFreeTrial(chat)
  );
}

export function chatStartedAsFreeTrialChat(
  user: IUserInfo,
  otherUser: IUserInfo,
  chat: IChatInfo
) {
  return (
    otherUser.type === EUserType.Featured &&
    user.type === EUserType.User &&
    !!chat.is_intro
  );
}

export function isChatVip(chat: IChatInfo, isPaidChatV2Enabled: boolean) {
  return (
    !isChatFreeTrial(chat) &&
    !!chat.other_user.is_vip &&
    isPaidChat(chat, isPaidChatV2Enabled)
  );
}

export function isDefaultFiltersSet(filtersSet: IDictionary<IChatFilter>) {
  return deepEqual(filtersSet, DEFAULT_CHAT_FILTERS_STATE);
}

export function isMatchingFiltersSet(
  filtersSet: IDictionary<IChatFilter>,
  otherFiltersSet: IDictionary<IChatFilter>
) {
  return deepEqual(filtersSet, otherFiltersSet);
}

export interface IGetChatsConfig {
  user: IUserInfo;
  providerConfig: IProviderConfig;
  isInitialRequest: boolean;
  isSneakyReload?: boolean;
  filtersSet?: IDictionary<IChatFilter>;
  altChatType?: EAltChatType;
}

export function getChatsUrl(config: IGetChatsConfig) {
  const { filtersSet, altChatType } = config;

  const volumeQueries = "?v=2&limit=20&offset=0"; // v=2 adds prospects to response
  let url = `${API_ROOT}/v1/chats${volumeQueries}`;

  if (filtersSet) {
    Object.keys(filtersSet).forEach((filterKeyString) => {
      const filterKey = filterKeyString as EChatFilterType;
      const filter = filtersSet[filterKey];
      const apiKey = TO_API_KEY[filterKey];
      const filterValue = filter.value as EChatFilterCategory;
      let apiValue = filter.value;
      if (
        defined(TO_API_VALUE[filterKey]) &&
        defined(TO_API_VALUE[filterKey]![filterValue])
      ) {
        apiValue = TO_API_VALUE[filterKey]![filterValue];
      }
      if (defined(apiValue) && apiValue !== "") {
        url += `&${apiKey}=${apiValue}`;
      }
    });
  }
  if (defined(altChatType)) {
    url += `&type=${altChatType}`;
  }
  return url;
}

// PRESENCE

function setPresenceOnArray(chats: IChatInfo[], pubnubActions: PubnubActions) {
  const onlineIds = chats
    .filter((chat: IChatInfo) => isOnline(chat.other_user.last_online))
    .map((chat: IChatInfo) => chat.other_user._id);
  addOnlineUsers(onlineIds, pubnubActions);
}

export function setPresence(chat: IChatInfo, pubnubActions: PubnubActions) {
  if (isOnline(chat.other_user.last_online)) {
    addOnlineUser(chat.other_user._id, pubnubActions);
  } else {
    removeOnlineUser(
      chat.other_user._id,
      chat.other_user.last_online,
      pubnubActions
    );
  }
}

// USER RELATIONSHIP TYPES

export const isUserToUser = (
  userType: EUserType,
  otherUserType: EUserType
): boolean => userType === EUserType.User && otherUserType === EUserType.User;
export const isFeaturedToFeatured = (
  userType: EUserType,
  otherUserType: EUserType
): boolean =>
  userType === EUserType.Featured && otherUserType === EUserType.Featured;
export const isUserToFeatured = (
  userType: EUserType,
  otherUserType: EUserType
): boolean =>
  userType === EUserType.User && otherUserType === EUserType.Featured;
export const isFeaturedToUser = (
  userType: EUserType,
  otherUserType: EUserType
): boolean =>
  userType === EUserType.Featured && otherUserType === EUserType.User;

export const isEligiblePaidChatPair = (
  viewer: Pick<IUserInfo, "type" | "is_staff">,
  otherUser: Pick<IUserInfo, "type" | "is_staff">
) =>
  (isFeaturedToUser(viewer.type, otherUser.type) ||
    isUserToFeatured(viewer.type, otherUser.type)) &&
  !viewer.is_staff &&
  !otherUser.is_staff;

export function isEligiblePaidChat(
  chat: IChatInfo | null | undefined,
  viewer: Pick<IUserInfo, "type" | "is_staff">,
  isPaidChatV2Enabled: boolean
) {
  return (
    !!chat &&
    isEligiblePaidChatPair(viewer, chat.other_user) &&
    isPaidChat(chat, isPaidChatV2Enabled)
  );
}
export const isFeaturedToFan = (
  user: IUserInfo,
  otherUserAffiliate: string
): boolean =>
  user.type === EUserType.Featured && user._id === otherUserAffiliate;
export const isUserStaff = (user: IUserInfo, otherUser: IUserInfo): boolean =>
  (isFeaturedToFeatured(user.type, otherUser.type) &&
    (user.is_staff || user.is_staff_mo)) ||
  (isFeaturedToUser(user.type, otherUser.type) && user.is_staff);
export const isUserSecretStaff = (
  user: IUserInfo,
  otherUser: IUserInfo
): boolean => isFeaturedToUser(user.type, otherUser.type) && !!user.is_staff_mo;
export const isOtherUserStaff = (
  user: IUserInfo,
  otherUser: IUserInfo
): boolean =>
  (isFeaturedToFeatured(user.type, otherUser.type) &&
    (otherUser.is_staff || otherUser.is_staff_mo)) ||
  (isUserToFeatured(user.type, otherUser.type) && otherUser.is_staff);

export const isSelfUser = (userId: string, otherUserId: string) =>
  userId === otherUserId;

// FREE TRIAL CHAT LOGIC

export function isFreeChat(
  userType: EUserType,
  userIsStaff: boolean,
  otherUserType: EUserType,
  otherUserCreditRate: number
) {
  return (
    isUserToUser(userType, otherUserType) || //user to user chat
    isFeaturedToFeatured(userType, otherUserType) || //fm to fm chat
    isFeaturedToUser(userType, otherUserType) ||
    userIsStaff || //chat with staff
    (otherUserType === EUserType.Featured && otherUserCreditRate === 0)
  ); //fm disabled billing
}

export function isFreeTrialChat(
  user: IUserInfo,
  otherUser: IUserInfo,
  selectedChat?: IChatInfo
) {
  if (otherUser.type !== EUserType.Featured || user.type !== EUserType.User) {
    return false;
  } else if (selectedChat) {
    return !selectedChat.intro_ended && selectedChat.is_intro;
  } else {
    return otherUser.intro_enabled && user.can_intro;
  }
}

interface ChatResponseWithOptionalData {
  data?: IChatInfo[];
  unread_messages?: number;
  unread_messages_favorites?: number;
}

export function transformChatResponse<T extends object>(
  res: T & ChatResponseWithOptionalData,
  config: IGetChatsConfig,
  pubnubActions: PubnubActions
): T & { data: IChatInfo[] } {
  if (res.unread_messages != null) {
    setUnreadMessages(res.unread_messages, pubnubActions);
  }
  if (res.unread_messages_favorites != null) {
    setUnreadMessagesFavorites(res.unread_messages_favorites, pubnubActions);
  }
  if (res.data) {
    setPresenceOnArray(res.data, pubnubActions);
    return res as T & { data: IChatInfo[] };
  }
  return { ...res, data: [] };
}

export function getChatsFilterCategoryLabel(category: EChatFilterCategory) {
  switch (category) {
    case EChatFilterCategory.All:
      return "Inbox";
    case EChatFilterCategory.Online:
      return "Online";
    case EChatFilterCategory.Fans:
      return "Fans";
    case EChatFilterCategory.Favorites:
      return "Favorites";
    case EChatFilterCategory.PaidChat:
      return "Paid Chat";
    case EChatFilterCategory.Recent:
      return "Recent";
    case EChatFilterCategory.Followers:
      return "Followers";
    case EChatFilterCategory.Following:
      return "Following";
    case EChatFilterCategory.Subscribers:
      return "Subscribers";
    case EChatFilterCategory.Subscribed:
      return "Subscribed";
    case EChatFilterCategory.Vip:
      return "VIP";
    case EChatFilterCategory.Unreplied:
      return "Unreplied";
    case EChatFilterCategory.Unread:
      return "Unread";
    case EChatFilterCategory.Sent:
      return "Sent";
    case EChatFilterCategory.Hidden:
      return "Hidden";
    case EChatFilterCategory.Creators:
      return "Creators";
    default:
      return "";
  }
}

export function getChatsFilterCategoryAmount(
  total: number,
  isEditing?: boolean,
  chatsSelection?: string[],
  isSelectAll?: boolean
) {
  if (!isEditing) {
    return total;
  }

  let amount;
  if (isSelectAll) {
    amount = total - chatsSelection!.length;
  } else {
    amount = chatsSelection!.length;
  }

  if (amount < 0 || isNaN(amount)) {
    return 0;
  }
  return amount;
}
